import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useApMismatchAlertSettings } from '@/context/services/ap_mismatch_alert_settings/ApMismatchAlertSettings.service';
import { toEnabledTargetKeys } from '@/context/services/ap_mismatch_alert_settings/utils';
import { useGetApPayeeAccountNumber } from '@/context/services/ap_payee/ApPayee.service';
import {
  getInferredApPayeeId,
  isInferredPayeeAccountNumberVerified,
  toApReportInferencesMap,
  type ApReportInference,
  type ApReportInferencesMap,
} from '@/context/services/report_inferences/utils';
import { useDataExtractionMismatchAlertFlag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertFlag';
import { RoleEnum } from 'ap-graphql';
import { useCallback, useMemo } from 'react';

type UseMismatchAlertReturn = {
  inferencesMap: ApReportInferencesMap;
  isFieldAlertEnabled: (targetKey: string) => boolean;
};

export const useMismatchAlert = (
  inferences: ApReportInference[],
  reportTypeId: string
): UseMismatchAlertReturn => {
  const isMismatchAlertFlag = useDataExtractionMismatchAlertFlag();
  const { currentOffice, currentOfficeMember } = useLoginInfo();
  const shouldSkipForMaskedAccountNumber =
    currentOffice?.apOfficeSetting?.isAccountNumberMasked &&
    !(currentOfficeMember?.officeMemberSetting?.role === RoleEnum.Admin);

  const settings = useApMismatchAlertSettings(isMismatchAlertFlag);

  const enabledTargetKeys = useMemo(
    () => toEnabledTargetKeys(settings.data ?? [], reportTypeId),
    [settings.data, reportTypeId]
  );

  const isFieldAlertEnabled = useCallback(
    (targetKey: string): boolean =>
      isMismatchAlertFlag && enabledTargetKeys.has(targetKey),
    [isMismatchAlertFlag, enabledTargetKeys]
  );

  const registeredAccountNumber = useGetApPayeeAccountNumber(
    isMismatchAlertFlag && !shouldSkipForMaskedAccountNumber
      ? getInferredApPayeeId(inferences)
      : undefined
  );

  const inferencesMap = useMemo(() => {
    const map = toApReportInferencesMap(inferences);

    if (shouldSkipForMaskedAccountNumber) {
      map.apPayee = undefined;
    }

    // The OCR-read account number is trusted only when it matches the account
    // number registered on the inferred payee (inference.ap_payee). Otherwise
    // there is nothing reliable to compare, so the alert is skipped.
    if (
      !isInferredPayeeAccountNumberVerified(
        registeredAccountNumber,
        map.apPayee?.value
      )
    ) {
      map.apPayee = undefined;
    }

    return map;
  }, [inferences, shouldSkipForMaskedAccountNumber, registeredAccountNumber]);

  return { inferencesMap, isFieldAlertEnabled };
};
